/**
 * A porta.
 *
 * É a transcrição de `gateway_page.dart` + `animated_door.dart` +
 * `passcode_input.dart` + `enrollment_panel.dart` + `chaves_da_casa.dart`: a
 * porta em arco com placa de latão, maçaneta e fechadura, quatro pontinhos e um
 * teclado de vidro, com pétalas caindo por cima enquanto ela está fechada.
 *
 * Dentro da folha cabe uma coisa de cada vez, e é o `Modo` que diz qual: quem
 * já tem senha vê o teclado; quem chega numa casa onde alguém ainda não
 * escolheu a dele vê o painel de primeiro acesso; e quem não conseguiu falar
 * com o servidor vê o recado com o botão de tentar de novo. São os três casos
 * do `_buildKeypadArea` de lá, na mesma ordem.
 *
 * Depois de escolhido o lugar não há campo de usuário: a senha de quatro
 * dígitos já diz quem é (o servidor resolve o slug a partir dela). O único
 * campo de texto da porta é o do nome, e só numa casa que ninguém configurou.
 */

import { useEffect, useRef, useState } from 'react';
import {
  ErroAPI,
  ligacao,
  nomeDaCasa,
  senhaFraca,
  traduzir,
  usarSessao,
  type AuthStatus,
  type Pessoa,
} from '@nosso-sempre/shared';
import { Icone } from '../componentes/Icone';
import { temSuporte, usar } from '../nucleo/chaves';
import './entrada.css';

const TAMANHO = 4;

/** O compasso do Flutter: destrava, a folha gira, e só então a casa aparece. */
const ABRE = 1500;
const REVELA = 1200;

/** `_rejectInput`: o erro fica na tela este tanto antes de a senha ser limpa. */
const ESPERA_DO_ERRO = 700;
/** Ida e volta do `AnimationController` do tranco, que dura mais que o erro. */
const TRANCO = 1040;

type Fase = 'fechada' | 'conferindo' | 'erro' | 'abrindo' | 'revelando';

/**
 * O `GatewayMode` de lá, mais os dois passos que no Flutter eram `AlertDialog`
 * por cima da porta. Aqui eles moram dentro da folha: o app não tem diálogo
 * nenhum, e inventar um só para estas duas telas seria desenho novo — que é
 * exatamente o que esta fase não faz.
 */
type Modo =
  | 'escolhendo'
  | 'criando'
  | 'repetindo'
  | 'senha'
  | 'semServidor'
  | 'codigoDaCasa'
  | 'pedirCodigo';

/* Os dois recados ficam em português aqui e passam pelo `traduzir()` no lugar em
   que viram estado: esta é a primeira linha do arquivo a ser lida, e ler o
   idioma antes de o núcleo ligar daria sempre português. */
const SEM_SERVIDOR =
  'Não foi possível falar com o servidor. Verifique a conexão e toque para tentar de novo.';
const SEM_BANCO = 'O app ainda não está conectado ao banco de dados. Toque para tentar de novo.';

interface Props {
  /**
   * Avisa que a porta está abrindo. A sessão nasce no instante em que a senha
   * confere, e sem isto a porta sairia da tela antes de girar — o `App` a
   * segura aqui até a animação terminar.
   */
  aoAbrir?: (abrindo: boolean) => void;
}

export function Entrada({ aoAbrir }: Props) {
  const { entrar, entrarComChave, cadastrar, casa, status, recarregarPorta } = usarSessao();
  const { api } = ligacao();

  // O `applyEnrollment` de lá, feito uma vez na abertura. Dá para ler o
  // `status` já aqui porque esta tela só é montada depois de a sessão tentar
  // buscá-lo: se ele ainda é nulo, é porque o pedido falhou — e aí a porta
  // abre direto no recado de "sem servidor", sem piscar uma roda de espera
  // que não estaria esperando nada. Mas é palpite, não sentença: quem chega
  // atrasado é atendido logo abaixo.
  const [modo, setModo] = useState<Modo>(() => leituraDaPorta(status).modo);
  const [recado, setRecado] = useState<string | null>(() => leituraDaPorta(status).recado);

  const [codigo, setCodigo] = useState('');
  const [fase, setFase] = useState<Fase>('fechada');
  /** Muda a cada erro só para o tranco recomeçar do zero. */
  const [tranco, setTranco] = useState(0);
  const [tremendo, setTremendo] = useState(false);
  /** O `restoreMessage` do `_rejectInput`: o que fica no lugar do erro depois. */
  const [restaurar, setRestaurar] = useState<string | null>(null);

  /** Em qual dos lugares vagos a senha está sendo criada. */
  const [lugar, setLugar] = useState<Pessoa | null>(null);
  const [nome, setNome] = useState('');
  /** A senha nova, esperando a segunda digitação bater com ela. */
  const [pendente, setPendente] = useState<string | null>(null);

  const [codigoDaCasa, setCodigoDaCasa] = useState('');
  const [codigoDigitado, setCodigoDigitado] = useState('');
  const [avisoDaCasa, setAvisoDaCasa] = useState<string | null>(null);
  /** Pedido em andamento nos botões do painel, para não sair dois iguais. */
  const [ocupadoNaPorta, setOcupadoNaPorta] = useState(false);

  const ocupado = fase === 'conferindo' || fase === 'abrindo' || fase === 'revelando';
  // `_acceptsDigits` + `_currentState == awaitingInput`: fora do teclado, e
  // durante os 700 ms do erro, tecla apertada não faz nada.
  const aceitaDigitos =
    (modo === 'senha' || modo === 'criando' || modo === 'repetindo') && fase === 'fechada';

  const pendentes = pendentesDe(status);
  const configurada = Boolean(casa?.configurada);
  // Casa que ninguém configurou ainda continua com os nomes de fábrica dentro
  // dela. Quem acabou de chegar não deve ver dois desconhecidos na porta:
  // escreve o próprio nome ali mesmo.
  const pedirNome = !configurada;
  // Só numa casa que ninguém configurou. Depois disso a porta é desta casa, e
  // sair dela por um link seria perder o que já está aqui dentro.
  const mostrarChaves = !configurada;
  const podePular = pendentes.length > 0 && pendentes.length < pessoasDe(status).length;
  // O `passkeysOffered` de lá, que é `canSignInWithPasskey && mode == unlock`:
  // o botão só existe quando este navegador sabe fazer a cerimônia e alguém já
  // criou uma chave. Anunciar antes disso seria oferecer o que não abre.
  const ofereceChave = modo === 'senha' && temSuporte() && temAlgumaChave(status);

  /**
   * O `status` do primeiro palpite chegou depois. A sessão insiste por trás da
   * porta quando o pedido de abertura falha, e é aqui que o que ela conseguir
   * entra na tela: sem isto, o palpite feito dentro do `useState` virava
   * sentença, e um tropeço de rede na abertura deixava a porta no "sem
   * servidor" até alguém tocar em "Tentar de novo" — de fora, um app que não
   * abre. Vale para a primeira resposta e só: depois dela, um "sem servidor"
   * na tela é o resultado de um pedido que a pessoa mandou, e desfazê-lo
   * sozinho seria mentir que deu certo.
   */
  const primeiraResposta = useRef(status !== null);
  useEffect(() => {
    if (primeiraResposta.current || !status) return;
    primeiraResposta.current = true;
    if (modo !== 'semServidor') return;
    const lido = leituraDaPorta(status);
    setModo(lido.modo);
    setRecado(lido.recado);
  }, [modo, status]);

  useEffect(() => {
    if (fase !== 'abrindo') return;
    const t = setTimeout(() => setFase('revelando'), ABRE);
    return () => clearTimeout(t);
  }, [fase]);

  useEffect(() => {
    if (fase !== 'revelando') return;
    const t = setTimeout(() => aoAbrir?.(false), REVELA);
    return () => clearTimeout(t);
  }, [fase, aoAbrir]);

  // A segunda metade do `_rejectInput`: a senha errada fica vermelha na tela
  // durante a espera e só então some. Limpar na hora tira do olho a única
  // pista de que os quatro dígitos chegaram a entrar.
  useEffect(() => {
    if (fase !== 'erro') return;
    const t = setTimeout(() => {
      setCodigo('');
      if (restaurar !== null) {
        setRecado(restaurar);
        setRestaurar(null);
      }
      setFase('fechada');
    }, ESPERA_DO_ERRO);
    return () => clearTimeout(t);
  }, [fase, tranco, restaurar]);

  // O tranco dura mais que o erro, então ele tem de acabar sozinho: preso à
  // fase, seria cortado no meio do caminho de volta.
  useEffect(() => {
    if (!tremendo) return;
    const t = setTimeout(() => setTremendo(false), TRANCO);
    return () => clearTimeout(t);
  }, [tremendo, tranco]);

  /** O `_rejectInput`: recado, tranco, e a senha apagada só no fim da espera. */
  function recusar(mensagem: string, restaurarCom: string | null = null) {
    setRecado(mensagem);
    setRestaurar(restaurarCom);
    setFase('erro');
    setTranco((n) => n + 1);
    setTremendo(true);
  }

  /** Como a pessoa é chamada no convite: o que ela escreveu, ou o nome do lugar. */
  function comoChamar(p: Pessoa | null): string {
    return nome.trim() || p?.nome || '';
  }

  async function destrancar(valor: string) {
    setFase('conferindo');
    aoAbrir?.(true);
    try {
      await entrar(valor);
      setRecado(null);
      setFase('abrindo');
    } catch (err) {
      aoAbrir?.(false);
      recusar(explicar(err));
    }
  }

  /**
   * `attemptPasskey`: a porta aberta pela chave em vez dos quatro dígitos.
   *
   * Quem mostra o pedido é o navegador, então aqui só se move a folha e se põe
   * o resultado embaixo do teclado. Não se diz quem é antes: a chave é
   * descobrível, e a credencial que volta já traz o dono.
   *
   * Desistir do pedido não é erro — o teclado simplesmente volta, sem recado.
   */
  async function entrarComAChave() {
    if (fase !== 'fechada') return;
    setCodigo('');
    setRecado(null);
    setFase('conferindo');
    aoAbrir?.(true);
    try {
      const cerimonia = await usar(await api.opcoesDeLogin());
      if (!cerimonia.ok) {
        aoAbrir?.(false);
        if (cerimonia.cancelada) setFase('fechada');
        else recusar(traduzir(cerimonia.recado ?? 'Não foi possível usar sua chave.'));
        return;
      }
      await entrarComChave(cerimonia.credencial);
      setFase('abrindo');
    } catch (err) {
      aoAbrir?.(false);
      recusar(explicarChave(err));
    }
  }

  /** `_handleCreate`: a primeira digitação, que ainda não é senha de ninguém. */
  function criar(valor: string) {
    if (senhaFraca(valor)) {
      recusar(traduzir('Esse código é fácil demais de adivinhar. Escolha outro.'));
      return;
    }
    setPendente(valor);
    setCodigo('');
    setModo('repetindo');
    setRecado(traduzir('Digite o mesmo código de novo para confirmar.'));
  }

  /** `_handleConfirm`: a segunda digitação, que é a que grava. */
  async function confirmar(valor: string) {
    if (valor !== pendente) {
      setPendente(null);
      setModo('criando');
      // Lá o convite é remontado com o nome do lugar; aqui vai o nome que a
      // pessoa escreveu, que é o mesmo que ela viu no botão. Mostrar de volta
      // um nome de fábrica que ela nunca digitou é o defeito que o próprio
      // painel de primeiro acesso se dá ao trabalho de evitar.
      recusar(
        traduzir('Os códigos não bateram. Vamos escolher de novo.'),
        convite(comoChamar(lugar)),
      );
      return;
    }
    if (!lugar) {
      setModo('escolhendo');
      recusar(traduzir('Algo deu errado. Tente de novo.'));
      return;
    }

    setFase('conferindo');
    aoAbrir?.(true);
    try {
      await cadastrar(lugar.slug, valor, nome.trim() || undefined);
      setRecado(null);
      setFase('abrindo');
    } catch {
      aoAbrir?.(false);
      setPendente(null);
      setModo('criando');
      recusar(traduzir('Não deu para salvar o código. Tente de novo.'));
    }
  }

  function apertar(digito: string) {
    if (!aceitaDigitos || ocupado) return;
    if (codigo.length >= TAMANHO) return;
    const novo = codigo + digito;
    setCodigo(novo);
    // Quatro dígitos já são a senha inteira; pedir um "entrar" depois disso é
    // um toque a mais por nada.
    if (novo.length < TAMANHO) return;
    if (modo === 'criando') criar(novo);
    else if (modo === 'repetindo') void confirmar(novo);
    else void destrancar(novo);
  }

  function apagar() {
    if (!aceitaDigitos || ocupado) return;
    setCodigo((c) => c.slice(0, -1));
  }

  // O teclado de verdade faz o mesmo que o de vidro. Sem lista de dependências
  // de propósito: o ouvinte precisa enxergar o `codigo` desta renderização, e
  // um só ouvinte por quadro é mais barato do que a conta de mantê-lo fixo.
  useEffect(() => {
    function ouvir(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (!aceitaDigitos) return;
      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        apertar(e.key);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        apagar();
      }
    }
    window.addEventListener('keydown', ouvir);
    return () => window.removeEventListener('keydown', ouvir);
  });

  /** `beginEnrollment`: daqui em diante a porta está criando uma senha. */
  function escolher(p: Pessoa) {
    setLugar(p);
    setPendente(null);
    setCodigo('');
    setModo('criando');
    setRecado(convite(comoChamar(p)));
  }

  /** `cancelEnrollment`, o "Voltar" debaixo do teclado. */
  function voltarAoPainel() {
    setLugar(null);
    setPendente(null);
    setCodigo('');
    setRestaurar(null);
    setFase('fechada');
    setModo('escolhendo');
    setRecado(null);
  }

  /** `switchToUnlock`: quem já tem senha não precisa passar pelo painel. */
  function pularParaSenha() {
    setLugar(null);
    setPendente(null);
    setCodigo('');
    setModo('senha');
    setRecado(null);
  }

  /**
   * `refreshEnrollment` + `applyEnrollment`: relê a porta e mostra o que vier.
   * É o que corre depois de trocar de casa, e é também o "Tentar de novo".
   */
  async function relerPorta() {
    setOcupadoNaPorta(true);
    try {
      const lido = leituraDaPorta(await recarregarPorta());
      setModo(lido.modo);
      setRecado(lido.recado);
      setCodigo('');
      setLugar(null);
      setPendente(null);
      setAvisoDaCasa(null);
    } catch {
      setModo('semServidor');
      setRecado(traduzir(SEM_SERVIDOR));
    } finally {
      setOcupadoNaPorta(false);
    }
  }

  /** `abrirCasaNova`: abre uma casa e mostra o código que liga a outra pessoa. */
  async function abrirCasa() {
    setOcupadoNaPorta(true);
    setAvisoDaCasa(null);
    try {
      const r = await api.abrirCasa();
      setCodigoDaCasa(r.codigo);
      setModo('codigoDaCasa');
    } catch (err) {
      setAvisoDaCasa(mensagemDoServidor(err, traduzir('Não deu para abrir a casa.')));
    } finally {
      setOcupadoNaPorta(false);
    }
  }

  /** `entrarComCodigo`: aponta este aparelho para uma casa que já existe. */
  async function entrarNaCasa() {
    const digitado = codigoDigitado.trim();
    if (!digitado) {
      setModo('escolhendo');
      return;
    }
    setOcupadoNaPorta(true);
    try {
      await api.entrarNaCasa(digitado);
      setCodigoDigitado('');
      await relerPorta();
    } catch (err) {
      setAvisoDaCasa(mensagemDoServidor(err, traduzir('Não deu para entrar na casa.')));
    } finally {
      setOcupadoNaPorta(false);
    }
  }

  /** O rótulo do botão do lugar, do `_rotuloDoLugar` de lá. */
  function rotuloDoLugar(p: Pessoa): string {
    if (!pedirNome) return p.nome;
    const escrito = nome.trim();
    if (escrito) return escrito;
    return pendentes.length > 1
      ? traduzir('Este lugar ({n})', { n: pendentes.indexOf(p) + 1 })
      : traduzir('Entrar');
  }

  // A porta é vista de fora, antes de haver sessão, e lá o nome da casa nunca
  // falta: o Flutter carrega o padrão embutido até o `GET /casa` responder.
  const gente = nomeDaCasa(casa);

  const classes = ['porta'];
  if (fase === 'erro') classes.push('erro');
  if (tremendo) classes.push('tremendo');
  if (fase === 'abrindo') classes.push('abrindo');
  if (fase === 'revelando') classes.push('revelando');

  function dentroDaFolha() {
    if (modo === 'semServidor') {
      return (
        <div className="painel">
          <span className="painel-nuvem" aria-hidden="true">
            <Icone nome="nuvem-cortada" />
          </span>
          <p className="painel-recado" role="status">
            {recado ?? traduzir('Sem conexão com o servidor.')}
          </p>
          <button
            type="button"
            className="lugar sozinho"
            disabled={ocupadoNaPorta}
            onClick={() => void relerPorta()}
          >
            <span className="lugar-nome">{traduzir('Tentar de novo')}</span>
          </button>
        </div>
      );
    }

    if (modo === 'codigoDaCasa') {
      return (
        <div className="painel painel-dialogo">
          <p className="painel-titulo">{traduzir('Nossa casa está aberta')}</p>
          <p className="painel-texto">{traduzir('Guarde este código:')}</p>
          <p className="codigo-casa">{codigoDaCasa}</p>
          <p className="painel-texto">
            {traduzir('É com ele que a outra pessoa entra na mesma casa, do aparelho dela.')}
          </p>
          <button
            type="button"
            className="lugar sozinho"
            disabled={ocupadoNaPorta}
            onClick={() => void relerPorta()}
          >
            <span className="lugar-nome">{traduzir('Entendi')}</span>
          </button>
        </div>
      );
    }

    if (modo === 'pedirCodigo') {
      return (
        <div className="painel painel-dialogo">
          <p className="painel-titulo">{traduzir('Código da casa')}</p>
          <label className="rotulo-campo" htmlFor="codigo-da-casa">
            {traduzir('Código')}
          </label>
          <input
            id="codigo-da-casa"
            className="campo-nome campo-codigo"
            autoFocus
            value={codigoDigitado}
            placeholder={traduzir('O que apareceu no outro aparelho')}
            onChange={(e) => setCodigoDigitado(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void entrarNaCasa();
            }}
          />
          {avisoDaCasa ? (
            <p className="painel-aviso" role="status">
              {avisoDaCasa}
            </p>
          ) : null}
          <div className="acoes-casa">
            <button type="button" className="link-porta" onClick={voltarAoPainel}>
              {traduzir('Cancelar')}
            </button>
            <button
              type="button"
              className="lugar curto"
              disabled={ocupadoNaPorta}
              onClick={() => void entrarNaCasa()}
            >
              <span className="lugar-nome">{traduzir('Entrar')}</span>
            </button>
          </div>
        </div>
      );
    }

    if (modo === 'escolhendo') {
      return (
        <div className="painel">
          <p className="painel-titulo">{traduzir('Primeiro acesso')}</p>
          <p className="painel-texto">
            {pedirNome
              ? traduzir('Como você quer aparecer nesta casa?')
              : traduzir('Quem está entrando?')}
          </p>

          {pedirNome ? (
            <>
              <input
                className="campo-nome"
                value={nome}
                maxLength={40}
                autoCapitalize="words"
                placeholder={traduzir('Seu nome')}
                aria-label={traduzir('Seu nome')}
                onChange={(e) => setNome(e.target.value)}
              />
              <p className="painel-dica">{traduzir('Depois dá para mudar tudo em Ajustes.')}</p>
            </>
          ) : null}

          <div className="lugares">
            {pendentes.map((p) => (
              <button
                key={p.slug}
                type="button"
                className="lugar"
                style={{ ['--cor-lugar' as string]: p.cor }}
                onClick={() => escolher(p)}
              >
                <span className="lugar-ponto" aria-hidden="true" />
                <span className="lugar-nome">{rotuloDoLugar(p)}</span>
              </button>
            ))}
          </div>

          {avisoDaCasa ? (
            <p className="painel-aviso" role="status">
              {avisoDaCasa}
            </p>
          ) : null}

          {mostrarChaves ? (
            <div className="atalhos-porta">
              <button
                type="button"
                className="link-porta"
                disabled={ocupadoNaPorta}
                onClick={() => void abrirCasa()}
              >
                {traduzir('Abrir nossa casa')}
              </button>
              <button
                type="button"
                className="link-porta"
                onClick={() => {
                  setAvisoDaCasa(null);
                  setModo('pedirCodigo');
                }}
              >
                {traduzir('Tenho um código de casa')}
              </button>
            </div>
          ) : null}

          {podePular ? (
            <button type="button" className="pular" onClick={pularParaSenha}>
              {traduzir('Já tenho meu código')}
            </button>
          ) : null}
        </div>
      );
    }

    return (
      <>
        {recado ? (
          <p className="recado" role="status">
            {recado}
          </p>
        ) : null}

        <div className="senha" key={tranco}>
          <div
            className="pontos"
            role="status"
            aria-label={traduzir('{feitos} de {total} dígitos', {
              feitos: codigo.length,
              total: TAMANHO,
            })}
          >
            {Array.from({ length: TAMANHO }, (_, i) => (
              <span key={i} className={i < codigo.length ? 'ponto-senha cheio' : 'ponto-senha'} />
            ))}
          </div>

          <div className="teclas">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
              <button
                key={d}
                type="button"
                className="tecla"
                disabled={ocupado}
                onClick={() => apertar(d)}
              >
                <span className="digito">{d}</span>
              </button>
            ))}
            <button
              type="button"
              className="tecla zero"
              disabled={ocupado}
              onClick={() => apertar('0')}
            >
              <span className="digito">0</span>
            </button>
            <button type="button" className="tecla apagar" disabled={ocupado} onClick={apagar}>
              <Icone nome="apagar" rotulo={traduzir('Apagar')} />
            </button>
          </div>
        </div>

        {ofereceChave ? (
          <button
            type="button"
            className="chave-porta"
            disabled={ocupado}
            onClick={() => void entrarComAChave()}
          >
            <Icone nome="digital" />
            {traduzir('Entrar com chave de acesso')}
          </button>
        ) : null}

        {modo !== 'senha' ? (
          <button type="button" className="voltar-porta" onClick={voltarAoPainel}>
            {traduzir('Voltar')}
          </button>
        ) : null}
      </>
    );
  }

  return (
    <main className={classes.join(' ')}>
      <div className="lente">
        <div className="quadro">
          <div className="respiro">
            <div className="halo" aria-hidden="true" />
            <div className="fagulhas" aria-hidden="true">
              {FAGULHAS.map((f, i) => (
                <span
                  key={i}
                  className="fagulha"
                  style={{ left: `${f.x}em`, top: `${f.y}em`, animationDelay: `${f.atraso}s` }}
                />
              ))}
            </div>

            <div className="clarao" aria-hidden="true" />
            <div className="batente" aria-hidden="true" />

            <div className="porta-folha">
              <div className="lavras" aria-hidden="true">
                <div className="lavra alta" />
                <div className="lavra baixa" />
              </div>

              <div className="placa">
                <div className="placa-corpo">
                  <span className="parafuso" aria-hidden="true" />
                  <h1 className="placa-texto">{traduzir('Nosso Sempre')}</h1>
                  <span className="parafuso" aria-hidden="true" />
                </div>
              </div>

              <p className="casal">{gente}</p>

              <div className="teclado">{dentroDaFolha()}</div>

              <div className="macaneta" aria-hidden="true">
                <span className="roseta" />
                <span className="braco" />
              </div>
              <div className="fechadura" aria-hidden="true">
                <span className="buraco" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="petalas" aria-hidden="true">
        {PETALAS.map((p, i) => (
          <span
            key={i}
            className="petala"
            style={{
              left: `${p.x}%`,
              animationDuration: `${p.queda}s`,
              animationDelay: `${p.atraso}s`,
            }}
          >
            <svg
              width={p.tamanho}
              height={p.tamanho * 2}
              viewBox="0 -15 15 30"
              style={{
                ['--balanco' as string]: `${p.balanco}vw`,
                animationDuration: '4s',
                animationDelay: `${p.giro}s`,
              }}
            >
              <path d="M0 0Q7.5 -15 15 0Q7.5 15 0 0" fill="rgba(244, 194, 194, 0.6)" />
            </svg>
          </span>
        ))}
      </div>
    </main>
  );
}

/** O convite do `beginEnrollment`, palavra por palavra. */
function convite(quem: string): string {
  return traduzir('Escolha um código de 4 dígitos, {quem}. Ele vai ser o seu para sempre.', {
    quem,
  });
}

function pessoasDe(s: AuthStatus | null): Pessoa[] {
  return s?.casa?.pessoas ?? [];
}

/**
 * Alguém já criou uma chave nesta casa?
 *
 * O `status` traz só a contagem por lugar, nunca a identidade da chave, e é de
 * propósito: a porta precisa saber se vale oferecer o botão, não de quem é a
 * chave. Quem é sai da própria credencial, depois.
 */
function temAlgumaChave(s: AuthStatus | null): boolean {
  return Object.values(s?.passkeys ?? {}).some((n) => n > 0);
}

/** Quem ainda não escolheu senha nesta casa, na ordem em que a casa os lista. */
function pendentesDe(s: AuthStatus | null): Pessoa[] {
  if (!s) return [];
  return pessoasDe(s).filter((p) => !s.enrolled[p.slug]);
}

/**
 * O `applyEnrollment` de lá: o que a porta mostra depende só do que o servidor
 * respondeu — se respondeu, se o banco está de pé, e se falta alguém escolher
 * senha.
 */
function leituraDaPorta(s: AuthStatus | null): { modo: Modo; recado: string | null } {
  if (!s) return { modo: 'semServidor', recado: traduzir(SEM_SERVIDOR) };
  if (!s.ready) return { modo: 'semServidor', recado: traduzir(SEM_BANCO) };
  if (pendentesDe(s).length === 0) return { modo: 'senha', recado: null };
  return { modo: 'escolhendo', recado: null };
}

/** O `message` do corpo de erro, que é o que a barrinha do Flutter mostrava. */
function mensagemDoServidor(err: unknown, padrao: string): string {
  return err instanceof ErroAPI && err.message ? err.message : padrao;
}

function explicar(err: unknown): string {
  if (err instanceof ErroAPI && err.status === 429) {
    const minutos = Math.max(1, Math.ceil((err.retryAfter ?? 900) / 60));
    return traduzir('Muitas tentativas. Tente de novo em {minutos} min.', { minutos });
  }
  if (err instanceof ErroAPI && err.status === 401) return traduzir('Essa senha não abre.');
  if (err instanceof ErroAPI && err.status === 400) return traduzir('A senha tem quatro números.');
  return traduzir('Não deu para falar com o servidor.');
}

/**
 * O mesmo que o `explicar`, menos as duas frases que só valem para os quatro
 * dígitos: uma chave recusada não é "essa senha não abre", e não há como
 * digitar números a mais nela.
 */
function explicarChave(err: unknown): string {
  if (err instanceof ErroAPI && err.status === 429) {
    const minutos = Math.max(1, Math.ceil((err.retryAfter ?? 900) / 60));
    return traduzir('Muitas tentativas. Tente de novo em {minutos} min.', { minutos });
  }
  if (err instanceof ErroAPI && err.status === 401)
    return traduzir('Essa chave não abre esta casa.');
  return traduzir('Não deu para falar com o servidor.');
}

/* ── O pó de ouro e as pétalas ───────────────────────────────────────────────
   O Flutter sorteia as pétalas com `math.Random()` e calcula as fagulhas a
   partir de `i * 137`. Aqui as duas listas são fixas: o mesmo desenho toda vez
   que a porta abre, e nada de sorteio no meio de uma renderização. */

const FAGULHAS = Array.from({ length: 26 }, (_, i) => {
  const semente = i * 137;
  return {
    x: +(((semente * 1.61) % 1) * 414).toFixed(1),
    y: +(((semente * 3.71) % 1) * 640).toFixed(1),
    atraso: +(-i * 0.11).toFixed(2),
  };
});

const PETALAS = Array.from({ length: 30 }, (_, i) => {
  // Um gerador pequeno e determinístico no lugar do `math.Random()` de lá.
  const sorte = (n: number) => {
    const v = Math.sin(i * 12.9898 + n * 78.233) * 43758.5453;
    return v - Math.floor(v);
  };
  const velocidade = sorte(1) * 0.5 + 0.5;
  return {
    x: +(sorte(2) * 100).toFixed(1),
    tamanho: +(sorte(3) * 15 + 10).toFixed(1),
    /** `1.5 / velocidade` voltas do relógio de 4 s, como no `% 1.5` de lá. */
    queda: +(6 / velocidade).toFixed(2),
    atraso: +(-sorte(4) * 12).toFixed(2),
    balanco: +((sorte(5) - 0.5) * 20).toFixed(2),
    /**
     * O `angle` de lá, que entra somado no giro e no bamboleio. Sem ele as
     * trinta pétalas giram no mesmo compasso, todas viradas para o mesmo lado
     * ao mesmo tempo — e aí não é chuva, é enfeite.
     */
    giro: +(-sorte(6) * 4).toFixed(2),
  };
});
